import type { PilotInfo } from './types';

// Pilot screening under the Old Republic policy (PB 24192901, Instruction/Rental).
// The policy's open-pilot warranty covers "any Student or Renter pilot approved by
// the Named Insured" — there is NO policy hour minimum. Screening here therefore
// flags only real issues for the owner's review (nobody auto-activates anyway):
// certificate level, ASEL rating, medical currency. Hour totals ride along as
// information, not as disqualifiers.
export const SCREENING = {
  // A make/model checkout before solo is AJM's own (and the lease's) requirement,
  // conducted by an instructor approved/named under the Policy.
  recentHoursMin: 5, // <5 hrs in make/model within 90 days → checkout emphasized
};

/** Student pilot certificate holders (Casey opened 66X to students 10/15/2026). */
export function isStudentPilot(p: Partial<Pick<PilotInfo, 'certificateType'>> | null | undefined): boolean {
  return /student/i.test(p?.certificateType || '');
}

export interface EligibilityResult {
  eligible: boolean;
  reasons: string[]; // items for the OWNER's review email (not shown to the pilot)
  needsCheckout: boolean; // recency note — checkout before solo is required regardless
}

function currentDate(dateStr?: string): boolean {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  return !Number.isNaN(d.getTime()) && d.getTime() >= Date.now();
}

export function checkEligibility(p: Partial<PilotInfo>): EligibilityResult {
  const reasons: string[] = [];
  const total = Number(p.totalHours) || 0;
  const makeModel = Number(p.hoursIn172) || 0;
  const cert = (p.certificateType || '').toLowerCase();
  const hasASEL = (p.ratings || []).some((r) => /single-?engine land/i.test(r));

  const student = isStudentPilot(p);
  if (!cert) reasons.push('No certificate type entered.');
  if (student)
    reasons.push(
      'Student pilot — allowed (Wings 10/15/2026), but only with a CFI named on PB222 who approves every flight in the portal before takeoff.'
    );
  // A student certificate carries no ratings; the ASEL check only applies to certificated pilots.
  if (!student && !hasASEL) reasons.push('Missing an Airplane Single-Engine Land rating.');
  if (!currentDate(p.medicalExpiration))
    reasons.push(student ? 'No current medical yet — fine for dual, required before solo.' : 'FAA medical is missing or expired.');
  if (total < 100)
    reasons.push(
      `Low total time (${total} hrs) — no policy minimum applies, but review experience before approval.`
    );
  if (makeModel < SCREENING.recentHoursMin)
    reasons.push(`Only ${makeModel} hrs in a 172 — make/model checkout before solo is essential.`);
  // Flight-review currency is the pilot's own responsibility — not collected or enforced here.

  const recent = Number(p.hoursLast90) || 0;
  const needsCheckout = recent < SCREENING.recentHoursMin;

  return { eligible: reasons.length === 0, reasons, needsCheckout };
}
